import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type HonoreeRow = {
  id: string
  name: string
  honoree_type: string | null
  company: string | null
  industry: string | null
  award: string | null
  gender: string | null
  event: {
    id: string
    name: string | null
    date: string | null
    organization_id: string
    organization_name: string
  }
}

type Props = {
  honorees: HonoreeRow[]
  filters: { search: string; honoree_type: string }
  filter_options: { honoree_types: string[] }
}

const ANY = '__any__'

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function ResearchHonoreesIndex({ honorees, filters, filter_options }: Props) {
  const [search, setSearch] = useState(filters.search)

  function applyFilters(next: Partial<typeof filters>) {
    const merged = { ...filters, search, ...next }
    router.get(
      '/admin/research/honorees',
      Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== '')),
      { preserveState: true },
    )
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    applyFilters({})
  }

  return (
    <>
      <Head title="Research - Honorees" />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearch} className="max-w-sm flex-1">
            <Input
              placeholder="Search by name or company..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </form>
          <Select
            value={filters.honoree_type || ANY}
            onValueChange={value => applyFilters({ honoree_type: value === ANY ? '' : value })}
          >
            <SelectTrigger className="w-36" size="sm">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent position="popper" align="end">
              <SelectGroup>
                <SelectItem value={ANY}>Type: All</SelectItem>
                {filter_options.honoree_types.map(type => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {honorees.length} {honorees.length === 1 ? 'honoree' : 'honorees'} · added from each
            event&rsquo;s Honorees section
          </p>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Professional Network</TableHead>
                <TableHead>Award</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {honorees.map(honoree => (
                <TableRow key={honoree.id}>
                  <TableCell className="font-medium">{honoree.name}</TableCell>
                  <TableCell>
                    {honoree.honoree_type ? (
                      <Badge variant="secondary">{honoree.honoree_type}</Badge>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{honoree.company || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{honoree.industry || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{honoree.award || '—'}</TableCell>
                  <TableCell>
                    <Link
                      href={`/admin/research/events/${honoree.event.id}`}
                      className="hover:underline"
                    >
                      {honoree.event.name || 'Untitled'}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <Link
                      href={`/admin/research/organizations/${honoree.event.organization_id}`}
                      className="hover:underline"
                    >
                      {honoree.event.organization_name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(honoree.event.date)}
                  </TableCell>
                </TableRow>
              ))}
              {honorees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    No honorees found. Add them from an event&rsquo;s Honorees section.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  )
}

ResearchHonoreesIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
